import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table, Tabs, Tab } from 'react-bootstrap';
import axios from '../utils/api';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const BuyerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const [orderNumberFilter, setOrderNumberFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [returnDialog, setReturnDialog] = useState({
    show: false,
    order: null,
    selectedItemIds: [],
    reasonCode: '',
    otherReason: '',
  });

  const returnReasonOptions = [
    { value: 'not_needed_any_more', label: 'Not needed any more' },
    { value: 'delivered_late', label: 'Delivered late' },
    { value: 'quality_not_good', label: 'Quality not good' },
    { value: 'item_not_same_as_described', label: 'Item not same as described' },
    { value: 'lower_price_elsewhere', label: 'Lower price elsewhere' },
    { value: 'other', label: 'Other' },
  ];

  const getReturnReasonLabel = (code, otherReason) => {
    const match = returnReasonOptions.find((entry) => entry.value === code);
    if (!match) return '-';
    if (code === 'other' && otherReason) {
      return `${match.label}: ${otherReason}`;
    }
    return match.label;
  };

  const getConditionLabel = (value) => {
    const mapping = { good: 'Good', fair: 'Fair', bad: 'Bad' };
    return mapping[(value || '').toLowerCase()] || '-';
  };

  const getRefundRecommendationLabel = (value) => {
    const mapping = { refund: 'Refund', no_refund: 'No Refund' };
    return mapping[(value || '').toLowerCase()] || '-';
  };

  const formatIndianAmount = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const formatISTDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
      hour12: true,
    }).format(parsed);
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/orders/me');
      setOrders(response.data || []);
    } catch {
      setError('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handlePrintInvoice = async (order) => {
    setActionError('');
    const invoiceWindow = window.open('about:blank', '_blank');

    if (!invoiceWindow) {
      setActionError('Allow pop-ups to open the printable invoice.');
      return;
    }

    invoiceWindow.opener = null;
    invoiceWindow.document.write('<p style="font-family:Segoe UI,Tahoma,sans-serif;padding:24px;">Preparing invoice...</p>');
    invoiceWindow.document.close();

    try {
      const response = await axios.get(`/api/orders/${order.id}/invoice`, { responseType: 'text' });
      invoiceWindow.document.open();
      invoiceWindow.document.write(response.data);
      invoiceWindow.document.close();
      invoiceWindow.focus();
    } catch (err) {
      invoiceWindow.close();
      setActionError(err.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handlePayNow = async (order) => {
    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);
    try {
      const scriptReady = await loadRazorpayScript();
      if (!scriptReady) throw new Error('Failed to load payment gateway. Please try again.');

      const { data: paymentData } = await axios.post(`/api/payments/retry/${order.id}`);

      await new Promise((resolve, reject) => {
        let paymentCompleted = false;

        const options = {
          key: paymentData.key_id,
          amount: paymentData.amount,
          currency: paymentData.currency,
          name: paymentData.name,
          description: paymentData.description,
          order_id: paymentData.razorpay_order_id,
          prefill: { name: paymentData.prefill_name, email: paymentData.prefill_email },
          handler: async (razorpayResponse) => {
            try {
              await axios.post('/api/payments/verify', {
                order_id: paymentData.order_id,
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
              });
              paymentCompleted = true;
              resolve();
            } catch (verifyErr) {
              reject(verifyErr);
            }
          },
          modal: {
            ondismiss: () => {
              if (!paymentCompleted) reject(new Error('Payment was cancelled.'));
            },
          },
          theme: { color: '#0d6efd' },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', () => {});
        razorpay.open();
      });

      setActionSuccess(`Payment successful for Order #${order.id.slice(0, 8)}`);
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Payment failed';
      setActionError(msg);
    } finally {
      setActionLoading('');
    }
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleCancelOrder = (order) => {
    openConfirmDialog(
      'Cancel Order',
      `Cancel Order #${order.id.slice(0, 8)}?`,
      'Cancel Order',
      async () => {
        setActionError('');
        setActionSuccess('');
        setActionLoading(order.id);
        try {
          await axios.post(`/api/orders/${order.id}/cancel`, {});
          setActionSuccess(`Order #${order.id.slice(0, 8)} has been cancelled.`);
          fetchOrders();
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || 'Failed to cancel order';
          setActionError(msg);
        } finally {
          setActionLoading('');
        }
      }
    );
  };

  const handleCancelOrderItem = (order, item) => {
    openConfirmDialog(
      'Cancel Order Item',
      `Cancel item ${item.product_name || item.product_id} from Order #${order.id.slice(0, 8)}?`,
      'Cancel Item',
      async () => {
        setActionError('');
        setActionSuccess('');
        setActionLoading(item.id);
        try {
          await axios.post(`/api/orders/${order.id}/cancel`, { item_ids: [item.id] });
          setActionSuccess('Order item cancelled successfully. Refund is initiated for paid orders.');
          fetchOrders();
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || 'Failed to cancel order item';
          setActionError(msg);
        } finally {
          setActionLoading('');
        }
      }
    );
  };

  const openReturnDialog = (order) => {
    const eligibleItems = (order.items || []).filter((item) => (item.status || 'active') !== 'cancelled');
    const selectedItemIds = eligibleItems.length === 1 ? [eligibleItems[0].id] : [];

    setReturnDialog({
      show: true,
      order,
      selectedItemIds,
      reasonCode: '',
      otherReason: '',
    });
  };

  const closeReturnDialog = () => {
    setReturnDialog({
      show: false,
      order: null,
      selectedItemIds: [],
      reasonCode: '',
      otherReason: '',
    });
  };

  const submitReturnRequest = async () => {
    const order = returnDialog.order;
    if (!order || !returnDialog.reasonCode) {
      return;
    }

    if (!returnDialog.selectedItemIds.length) {
      setActionError('Please select at least one item to return.');
      return;
    }

    if (returnDialog.reasonCode === 'other' && !returnDialog.otherReason.trim()) {
      setActionError('Please enter the other reason for return.');
      return;
    }

    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);
    closeReturnDialog();

    try {
      const payload = {
        reason_code: returnDialog.reasonCode,
        other_reason: returnDialog.reasonCode === 'other' ? returnDialog.otherReason.trim() : null,
        item_ids: returnDialog.selectedItemIds,
      };

      await axios.post(`/api/orders/${order.id}/request-return`, payload);
      setActionSuccess('Return requested for selected items. We will arrange pickup soon.');
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to request return';
      setActionError(msg);
    } finally {
      setActionLoading('');
    }
  };

  const orderStatusOptions = useMemo(() => {
    return Array.from(new Set((orders || []).map((order) => (order.status || '').trim().toLowerCase()).filter(Boolean))).sort();
  }, [orders]);

  const paymentStatusOptions = useMemo(() => {
    return Array.from(new Set((orders || []).map((order) => (order.payment_status || '').trim().toLowerCase()).filter(Boolean))).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const normalizedOrderQuery = orderNumberFilter.trim().toLowerCase();
    const fromDate = fromDateFilter ? new Date(`${fromDateFilter}T00:00:00`) : null;
    const toDate = toDateFilter ? new Date(`${toDateFilter}T23:59:59.999`) : null;

    const matchesFilters = (order) => {
      const fullOrderId = (order.id || '').toLowerCase();
      const shortOrderId = fullOrderId.slice(0, 8);
      const orderStatus = (order.status || '').trim().toLowerCase();
      const paymentStatus = (order.payment_status || '').trim().toLowerCase();
      const createdAt = order.created_at ? new Date(order.created_at) : null;

      if (normalizedOrderQuery && !fullOrderId.includes(normalizedOrderQuery) && !shortOrderId.includes(normalizedOrderQuery)) {
        return false;
      }

      if (orderStatusFilter && orderStatus !== orderStatusFilter) {
        return false;
      }

      if (paymentStatusFilter && paymentStatus !== paymentStatusFilter) {
        return false;
      }

      if (fromDate && (!createdAt || Number.isNaN(createdAt.getTime()) || createdAt < fromDate)) {
        return false;
      }

      if (toDate && (!createdAt || Number.isNaN(createdAt.getTime()) || createdAt > toDate)) {
        return false;
      }

      return true;
    };

    const filtered = (orders || []).filter(matchesFilters);
    filtered.sort((a, b) => {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });

    return filtered;
  }, [
    orders,
    orderNumberFilter,
    orderStatusFilter,
    paymentStatusFilter,
    fromDateFilter,
    toDateFilter,
    sortOrder,
  ]);

  const clearFilters = () => {
    setOrderNumberFilter('');
    setOrderStatusFilter('');
    setPaymentStatusFilter('');
    setFromDateFilter('');
    setToDateFilter('');
    setSortOrder('newest');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading orders...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>My Orders</h1>
          <p className="text-muted mb-0">Review your purchases and the delivery address captured for each order.</p>
        </Col>
        <Col xs="auto" className="d-flex align-items-start">
          <Button variant="outline-secondary" onClick={fetchOrders} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Orders'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>}
      {actionSuccess && <Alert variant="success" dismissible onClose={() => setActionSuccess('')}>{actionSuccess}</Alert>}

      {orders.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No orders yet</h5>
            <p className="text-muted mb-0">Your completed purchases will appear here.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={4} lg={3}>
                  <Form.Label className="mb-1">Order Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by order ID"
                    value={orderNumberFilter}
                    onChange={(e) => setOrderNumberFilter(e.target.value)}
                  />
                </Col>
                <Col md={4} lg={2}>
                  <Form.Label className="mb-1">Order Status</Form.Label>
                  <Form.Select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {orderStatusOptions.map((statusOption) => (
                      <option key={statusOption} value={statusOption}>{statusOption}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4} lg={2}>
                  <Form.Label className="mb-1">Payment Status</Form.Label>
                  <Form.Select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {paymentStatusOptions.map((statusOption) => (
                      <option key={statusOption} value={statusOption}>{statusOption}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6} lg={2}>
                  <Form.Label className="mb-1">From Date</Form.Label>
                  <Form.Control type="date" value={fromDateFilter} onChange={(e) => setFromDateFilter(e.target.value)} />
                </Col>
                <Col md={6} lg={2}>
                  <Form.Label className="mb-1">To Date</Form.Label>
                  <Form.Control type="date" value={toDateFilter} onChange={(e) => setToDateFilter(e.target.value)} />
                </Col>
                <Col md={6} lg={2}>
                  <Form.Label className="mb-1">Sort</Form.Label>
                  <Form.Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                    <option value="newest">Newest to oldest</option>
                    <option value="oldest">Oldest to newest</option>
                  </Form.Select>
                </Col>
                <Col md={6} lg={1} className="d-grid">
                  <Button variant="outline-secondary" onClick={clearFilters}>Clear</Button>
                </Col>
                <Col md={12} lg={12}>
                  <div className="text-muted small">
                    {filteredOrders.length} result{filteredOrders.length === 1 ? '' : 's'} found
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {filteredOrders.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <h5>No matching orders</h5>
                <p className="text-muted mb-0">Try changing filters or clearing them.</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredOrders.map((order) => (
            <Col xs={12} key={order.id}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div className="fw-bold">Order #{order.id}</div>
                    <small className="text-muted d-block">Invoice #{order.invoice_number || '-'}</small>
                    <small className="text-muted">Placed on {formatISTDateTime(order.created_at)} IST</small>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge bg="secondary">{order.status}</Badge>
                    <Badge bg={order.payment_status === 'paid' ? 'success' : order.payment_status === 'payment_failed' ? 'danger' : order.payment_status === 'cancelled' ? 'secondary' : order.payment_status?.includes('refunded') ? 'info' : 'warning'}>
                      {order.payment_status || 'payment_pending'}
                    </Badge>
                    <span className="fw-semibold">{order.currency} {formatIndianAmount(order.total_amount)}</span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handlePrintInvoice(order)}
                    >
                      Print Invoice
                    </Button>
                    {(order.payment_status === 'payment_pending' || order.payment_status === 'payment_failed') && (
                      <>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={actionLoading === order.id}
                          onClick={() => handlePayNow(order)}
                        >
                          {actionLoading === order.id ? <Spinner size="sm" animation="border" /> : 'Pay Now'}
                        </Button>
                      </>
                    )}
                    {['confirmed', 'packaging'].includes((order.status || '').toLowerCase()) && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={actionLoading === order.id}
                        onClick={() => handleCancelOrder(order)}
                      >
                        Cancel Order
                      </Button>
                    )}
                    {(order.status || '').toLowerCase() === 'delivered' && !order.return_requested_at && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        disabled={actionLoading === order.id}
                        onClick={() => openReturnDialog(order)}
                      >
                        {actionLoading === order.id ? <Spinner size="sm" animation="border" /> : 'Request Return (Order)'}
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="g-4">
                    <Col lg={8}>
                      <h6 className="mb-3">Items</h6>
                      <Table responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Variant SKU</th>
                            <th>Size</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {item.thumbnail_url ? (
                                    <img
                                      src={item.thumbnail_url}
                                      alt={item.product_name || item.product_id}
                                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                    />
                                  ) : (
                                    <div style={{ width: 40, height: 40, background: '#e9ecef', borderRadius: 4, flexShrink: 0 }} />
                                  )}
                                  <span>{item.product_name || item.product_id}</span>
                                </div>
                              </td>
                              <td>{item.variant_sku || 'Base product'}</td>
                              <td>{item.size_name || '-'}</td>
                              <td>{item.quantity}</td>
                              <td>{item.currency} {formatIndianAmount(item.subtotal ?? ((Number(item.price) || 0) * (item.quantity || 0)))}</td>
                              <td>
                                {item.status === 'cancelled' ? (
                                  <Badge bg="secondary">cancelled</Badge>
                                ) : item.status === 'return_requested' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <small className="fw-semibold text-dark">return requested</small>
                                    <small className="text-muted">{getReturnReasonLabel(item.return_reason_code, item.return_reason_other)}</small>
                                  </div>
                                ) : item.status === 'return_picked_up' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <small className="fw-semibold text-dark">return picked up</small>
                                    <small className="text-muted">Picked up by courier</small>
                                  </div>
                                ) : item.status === 'return_received' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <small className="fw-semibold text-dark">return received</small>
                                    <small className="text-muted">Condition: {getConditionLabel(item.return_condition)}</small>
                                    <small className="text-muted">Recommendation: {getRefundRecommendationLabel(item.return_refund_recommendation)}</small>
                                  </div>
                                ) : (
                                  <div className="d-flex gap-2 flex-wrap">
                                    {(order.status !== 'shipped' && order.status !== 'delivered') && (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={actionLoading === item.id}
                                        onClick={() => handleCancelOrderItem(order, item)}
                                      >
                                        {actionLoading === item.id ? <Spinner size="sm" animation="border" /> : 'Cancel Item'}
                                      </Button>
                                    )}
                                    {(order.payment_status === 'paid' || order.payment_status === 'partially_refunded' || order.payment_status === 'fully_refunded') && (
                                      <Button
                                        as={Link}
                                        to={`/buyer/product/${item.product_id}?review=1`}
                                        size="sm"
                                        variant="outline-primary"
                                      >
                                        Write Review
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col lg={4}>
                      <Tabs
                        id={`buyer-order-tabs-${order.id}`}
                        defaultActiveKey="summary"
                        className="mb-3"
                      >
                        <Tab eventKey="summary" title="Purchase Summary">
                          <div className="border rounded p-3 bg-light-subtle">
                            <div className="d-flex justify-content-between py-1">
                              <span>Price Total</span>
                              <span>{order.currency} {formatIndianAmount(order.price_total)}</span>
                            </div>
                            {Number(order.cgst_amount) > 0 || Number(order.sgst_amount) > 0 ? (
                              <>
                                <div className="d-flex justify-content-between py-1">
                                  <span>CGST</span>
                                  <span>{order.currency} {formatIndianAmount(order.cgst_amount)}</span>
                                </div>
                                <div className="d-flex justify-content-between py-1">
                                  <span>SGST</span>
                                  <span>{order.currency} {formatIndianAmount(order.sgst_amount)}</span>
                                </div>
                              </>
                            ) : (
                              <div className="d-flex justify-content-between py-1">
                                <span>IGST</span>
                                <span>{order.currency} {formatIndianAmount(order.igst_amount)}</span>
                              </div>
                            )}
                            <div className="d-flex justify-content-between pt-2 mt-2 border-top fw-semibold text-primary">
                              <span>Grand Total</span>
                              <span>{order.currency} {formatIndianAmount(order.total_amount)}</span>
                            </div>

                            <div className="d-flex justify-content-between pt-2 mt-2 border-top">
                              <span>Refund Status</span>
                              <span className="text-capitalize">{(order.refund_status || 'not_required').replace('_', ' ')}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>Refunded Amount</span>
                              <span>{order.currency} {formatIndianAmount(order.refunded_amount || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>Refunded At</span>
                              <span>{formatISTDateTime(order.refunded_at)}</span>
                            </div>
                          </div>
                        </Tab>

                        <Tab eventKey="delivery" title="Delivery Snapshot">
                          <div className="border rounded p-3 bg-light-subtle">
                            <div className="fw-semibold">{order.shipping_contact_name || '-'}</div>
                            <div>{order.shipping_contact_isd_code || ''} {order.shipping_contact_number || ''}</div>
                            <div>{order.shipping_addr_line1}</div>
                            {order.shipping_addr_line2 && <div>{order.shipping_addr_line2}</div>}
                            <div>{order.shipping_city}, {order.shipping_state}</div>
                            <div>{order.shipping_country} - {order.shipping_pincode}</div>
                            <hr />
                            <div><strong>Delivery Date:</strong> {formatISTDateTime(order.delivery_date)}</div>
                            <div><strong>Delivered To:</strong> {order.delivered_to || '-'}</div>
                          </div>
                        </Tab>

                        <Tab eventKey="returns" title="Return Snapshot">
                          {(
                            order.return_requested_at ||
                            order.return_pickup_date ||
                            order.return_received_date ||
                            (order.items || []).some((item) => item.return_reason_code || item.return_pickup_confirmed || item.return_received_confirmed)
                          ) ? (
                            <div className="border rounded p-3 bg-light-subtle">
                              <div className="mb-2">
                                <strong>1. Return Requested</strong>
                              </div>
                              <div className="small text-muted mb-2">
                                {formatISTDateTime(order.return_requested_at)}
                              </div>

                              <div className="mb-2">
                                <strong>2. Picked Up By Courier</strong>
                              </div>
                              <div className="small text-muted mb-2">
                                {formatISTDateTime(order.return_pickup_date)}
                              </div>

                              <div className="mb-2">
                                <strong>3. Received At Store</strong>
                              </div>
                              <div className="small text-muted mb-2">
                                {formatISTDateTime(order.return_received_date)}
                              </div>

                              <div className="mb-2">
                                <strong>4. Refund Recommendation</strong>
                              </div>
                              <div className="small text-muted mb-2">
                                {getRefundRecommendationLabel(order.return_refund_recommendation)}
                              </div>

                              <div className="mb-2">
                                <strong>5. Final Status</strong>
                              </div>
                              <div className="small text-muted mb-2">
                                {(order.status || '-').replace(/_/g, ' ')}
                              </div>

                              {/* {(order.items || []).some((item) => item.return_reason_code) && <hr />}
                              {(order.items || [])
                                .filter((item) => item.return_reason_code)
                                .map((item) => (
                                  <div key={`timeline-${item.id}`} className="small mb-2 pb-2 border-bottom">
                                    <div className="fw-semibold">{item.product_name || item.product_id}</div>
                                    <div>Reason: {getReturnReasonLabel(item.return_reason_code, item.return_reason_other)}</div>
                                    <div>Condition: {getConditionLabel(item.return_condition)}</div>
                                    <div>Recommendation: {getRefundRecommendationLabel(item.return_refund_recommendation)}</div>
                                  </div>
                                ))} */}
                            </div>
                          ) : (
                            <div className="border rounded p-3 bg-light-subtle text-muted">
                              No return activity for this order yet.
                            </div>
                          )}
                        </Tab>
                      </Tabs>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
              ))}
            </Row>
          )}
        </>
      )}

      <Modal show={confirmDialog.show} onHide={closeConfirmDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog.title || 'Confirm Action'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirmDialog}>
            Keep
          </Button>
          <Button variant="danger" onClick={executeConfirmDialogAction}>
            {confirmDialog.confirmText || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={returnDialog.show} onHide={closeReturnDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request Order Return</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            You can submit return request only once for this order.
          </div>

          <div className="mb-3">
            <div className="fw-semibold mb-2">Select items to return</div>
            <Table responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Select</th>
                  <th>Item</th>
                  <th>Variant SKU</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {(returnDialog.order?.items || []).map((item) => {
                  const isCancelled = (item.status || 'active') === 'cancelled';
                  const checked = returnDialog.selectedItemIds.includes(item.id);

                  return (
                    <tr key={`return-item-${item.id}`}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          disabled={isCancelled}
                          onChange={(event) => {
                            const shouldCheck = event.target.checked;
                            setReturnDialog((current) => {
                              const selected = new Set(current.selectedItemIds);
                              if (shouldCheck) {
                                selected.add(item.id);
                              } else {
                                selected.delete(item.id);
                              }
                              return { ...current, selectedItemIds: Array.from(selected) };
                            });
                          }}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {item.thumbnail_url ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.product_name || item.product_id}
                              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{ width: 36, height: 36, background: '#e9ecef', borderRadius: 4, flexShrink: 0 }} />
                          )}
                          <div>
                            <div>{item.product_name || item.product_id}</div>
                            {isCancelled && <small className="text-muted">Cancelled item cannot be returned</small>}
                          </div>
                        </div>
                      </td>
                      <td>{item.variant_sku || 'Base product'}</td>
                      <td>{item.size_name || '-'}</td>
                      <td>{item.quantity}</td>
                      <td>{item.currency} {formatIndianAmount(item.subtotal ?? ((Number(item.price) || 0) * (item.quantity || 0)))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Reason for return</Form.Label>
            <Form.Select
              value={returnDialog.reasonCode}
              onChange={(event) => setReturnDialog((current) => ({ ...current, reasonCode: event.target.value }))}
            >
              <option value="">Select reason</option>
              {returnReasonOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {returnDialog.reasonCode === 'other' && (
            <Form.Group>
              <Form.Label>Other reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={returnDialog.otherReason}
                onChange={(event) => setReturnDialog((current) => ({ ...current, otherReason: event.target.value }))}
                placeholder="Enter return reason"
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReturnDialog}>Cancel</Button>
          <Button
            variant="warning"
            onClick={submitReturnRequest}
            disabled={
              !returnDialog.selectedItemIds.length ||
              !returnDialog.reasonCode ||
              (returnDialog.reasonCode === 'other' && !returnDialog.otherReason.trim())
            }
          >
            Submit Return Request
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BuyerOrdersPage;
